import React, { useEffect, useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import PageShell from '../Components/ui/PageShell';
import DashboardCard from '../Components/ui/DashboardCard';
import DataTable from '../Components/ui/DataTable';
import StatusBadge from '../Components/ui/StatusBadge';
import AppModal from '../Components/ui/AppModal';
import PriorityBadge from '../Components/ui/PriorityBadge';
import RequirementJdActions from '../Components/requirements/RequirementJdActions';
import { getDecryptedData, getUserType } from '../utils/authStorage';
import { getTodayMonitor } from '../services/workflowService';
import { getRequirementById } from '../services/requirementService';
import { getApiErrorMessage } from '../utils/apiError';
import { showError } from '../utils/toast';
import { formatDisplayDate } from '../utils/dateFormat';
import {
  MONITOR_CARD_FILTERS,
  MONITOR_FILTER_LABELS,
  MONITOR_REASON_LABELS,
  filterMonitorItemsForCard,
} from '../utils/todayMonitor';

const EMPTY_MONITOR_ITEMS = [];

function formatWhyToday(row) {
  const reasons = Array.isArray(row.reasons) && row.reasons.length
    ? row.reasons
    : [row.reason];
  return reasons
    .map((reason) => MONITOR_REASON_LABELS[reason] || reason)
    .filter(Boolean)
    .join(', ');
}

function TodayWorkMonitor() {
  const userId = getDecryptedData('Id');
  const userType = getUserType();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('all');
  const [cardModal, setCardModal] = useState({ show: false, key: '', title: '' });
  const [viewReq, setViewReq] = useState(null);
  const [showReq, setShowReq] = useState(false);
  const [viewReqLoading, setViewReqLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await getTodayMonitor(userId);
        setData(response);
      } catch (error) {
        console.error('Today monitor load failed:', error);
        setLoadError(error.message || 'Failed to load today work monitor.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const summary = data?.summary || {};
  const allItems = data?.items || EMPTY_MONITOR_ITEMS;

  const filteredItems = useMemo(() => {
    if (filter === 'all') return allItems;
    return allItems.filter(
      (item) => item.reason === filter || (item.reasons || []).includes(filter),
    );
  }, [allItems, filter]);

  const cardModalRows = useMemo(
    () => filterMonitorItemsForCard(allItems, cardModal.key),
    [allItems, cardModal.key],
  );

  const openCardModal = (cardKey, title) => {
    setCardModal({ show: true, key: cardKey, title });
  };

  const closeCardModal = () => {
    setCardModal({ show: false, key: '', title: '' });
  };

  const viewRequirement = async (requirementId, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!requirementId) return;

    setViewReqLoading(true);
    try {
      const requirement = await getRequirementById(requirementId);
      setViewReq(requirement);
      setShowReq(true);
    } catch (error) {
      console.error('Error fetching requirement:', error);
      showError(getApiErrorMessage(error, 'Unable to open this requirement.'));
    } finally {
      setViewReqLoading(false);
    }
  };

  const closeRequirementModal = () => {
    setShowReq(false);
    setViewReq(null);
  };

  const columns = useMemo(() => [
    { key: 'candidateName', label: 'Source Profile', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status', useStoredLabel: true },
    { key: 'recruiter', label: 'Recruiter', sortable: true },
    {
      key: 'reason',
      label: 'Why Today',
      sortable: true,
      render: (row) => formatWhyToday(row),
    },
    {
      key: 'priority',
      label: 'Follow-up Priority',
      sortable: true,
      render: (row) => <StatusBadge status={row.priority} />,
    },
    { key: 'interviewLabel', label: 'Interview', sortable: true },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          disabled={!row.requirementId || viewReqLoading}
          onClick={(event) => viewRequirement(row.requirementId, event)}
        >
          View Req
        </Button>
      ),
    },
  ], [viewReqLoading]);

  const pageColumns = useMemo(
    () => columns.filter((column) => column.key !== 'requirementId'),
    [columns],
  );

  const subtitle = userType === 'Admin'
    ? 'Profiles across the organization that need follow-up today (IST).'
    : 'Profiles from your team and assigned clients that need follow-up today (IST).';

  return (
    <PageShell
      title="Today Work Monitor"
      subtitle={subtitle}
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Today Work' },
      ]}
      backTo="/dashboard"
    >
      {loadError && <div className="alert alert-danger">{loadError}</div>}

      <div className="hr-stat-grid">
        <DashboardCard
          title="Total to Monitor"
          value={summary.total || 0}
          icon="📋"
          accent="blue"
          onClick={() => openCardModal(MONITOR_CARD_FILTERS.total, MONITOR_FILTER_LABELS.total)}
        />
        <DashboardCard
          title="High Priority"
          value={summary.highPriority || 0}
          icon="🔥"
          accent="orange"
          onClick={() => openCardModal(MONITOR_CARD_FILTERS.highPriority, MONITOR_FILTER_LABELS.high_priority)}
        />
        <DashboardCard
          title="Interviews Today"
          value={summary.interviewsToday || 0}
          icon="🎤"
          accent="teal"
          onClick={() => openCardModal(MONITOR_CARD_FILTERS.interviewsToday, MONITOR_FILTER_LABELS.interviews_today)}
        />
        <DashboardCard
          title="Pending Action"
          value={summary.pendingAction || 0}
          icon="⏳"
          accent="gray"
          onClick={() => openCardModal(MONITOR_CARD_FILTERS.pendingAction, MONITOR_FILTER_LABELS.pending_action)}
        />
        <DashboardCard
          title="Offer Stage"
          value={summary.offerStage || 0}
          icon="📨"
          accent="green"
          onClick={() => openCardModal(MONITOR_CARD_FILTERS.offerStage, MONITOR_FILTER_LABELS.offer_stage)}
        />
      </div>
      <p className="hr-section-subtitle" style={{ marginTop: '-8px', marginBottom: '16px' }}>
        Click a card to review matching profiles. Card counts use today&apos;s IST queue and stay the same when you search or filter the table below.
      </p>

      <div className="hr-section-toolbar">
        <h3 className="hr-section-title" style={{ margin: 0 }}>Profiles to Monitor</h3>
        <select
          className="form-select hr-filter-select"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter monitor queue"
        >
          <option value="all">All Reasons</option>
          <option value="interview_today">Interview Today</option>
          <option value="pending_action">Pending Action</option>
          <option value="offer_stage">Offer Stage</option>
          <option value="uploaded_today">Uploaded Today</option>
          <option value="status_updated_today">Status Updated Today</option>
        </select>
      </div>

      <div className="hr-card" style={{ padding: '16px' }}>
        {loading ? (
          <p className="hr-empty-state">Loading today&apos;s monitor queue...</p>
        ) : (
          <DataTable
            columns={pageColumns}
            data={filteredItems}
            rowKey="candidateId"
            searchKeys={['candidateName', 'client', 'regId', 'role', 'recruiter', 'status']}
            searchPlaceholder="Search candidate, client, or req id"
            emptyMessage="No profiles need monitoring today."
            exportFileName="today-work-monitor.csv"
            hideImport
          />
        )}
      </div>

      <AppModal
        show={cardModal.show}
        onHide={closeCardModal}
        title={cardModal.title}
        subtitle={`${cardModalRows.length} record${cardModalRows.length === 1 ? '' : 's'}`}
        size="xl"
        footer={(
          <Button variant="secondary" onClick={closeCardModal}>
            Close
          </Button>
        )}
      >
        <DataTable
          columns={columns}
          data={cardModalRows}
          rowKey="candidateId"
          searchKeys={['candidateName', 'client', 'regId', 'role', 'recruiter', 'status']}
          searchPlaceholder="Search candidate, client, or req id"
          emptyMessage={`No profiles match ${cardModal.title || 'this card'} for today.`}
          exportFileName="today-work-monitor.csv"
          hideImport
          pageSize={10}
        />
      </AppModal>

      <AppModal
        show={showReq}
        onHide={closeRequirementModal}
        title="Requirement Details"
        subtitle={viewReq?.regId ? `Req ID: ${viewReq.regId}` : ''}
        size="lg"
        footer={(
          <Button variant="secondary" onClick={closeRequirementModal}>
            Close
          </Button>
        )}
      >
        {viewReq ? (
          <Table bordered hover responsive className="hr-table">
            <tbody>
              <tr><th>Reg ID</th><td>{viewReq.regId}</td></tr>
              <tr><th>Client</th><td>{viewReq.client}</td></tr>
              <tr><th>Role</th><td>{viewReq.role}</td></tr>
              <tr><th>Requirement Type</th><td>{viewReq.requirementtype || '—'}</td></tr>
              <tr><th>Priority</th><td><PriorityBadge priority={viewReq.priority} /></td></tr>
              <tr><th>Location</th><td>{viewReq.location || '—'}</td></tr>
              <tr><th>Skill</th><td>{viewReq.skill || '—'}</td></tr>
              <tr><th>Type of Contract</th><td>{viewReq.typeOfContract || '—'}</td></tr>
              <tr><th>Start Date</th><td>{formatDisplayDate(viewReq.startDate)}</td></tr>
              <tr><th>Uploaded Date</th><td>{formatDisplayDate(viewReq.uploadedDate)}</td></tr>
              <tr>
                <th>JD PDF</th>
                <td><RequirementJdActions requirement={viewReq} /></td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <p className="hr-empty-state">No requirement details to display.</p>
        )}
      </AppModal>
    </PageShell>
  );
}

export default TodayWorkMonitor;
